import { useEffect, useState } from 'react';
import { DownloadIcon, DesktopIcon, PhoneIcon, ExternalLinkIcon, LockIcon, CloudOffIcon, ZapIcon, GlobeIcon } from '@audiio/icons';
import { useDevMode } from '../hooks/useDevMode';
import './Download.css';

type Platform = 'mac' | 'mac-intel' | 'windows' | 'linux' | 'unknown';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'unknown';

  const userAgent = navigator.userAgent.toLowerCase();
  const platform = (navigator as any).userAgentData?.platform?.toLowerCase() || navigator.platform.toLowerCase();

  if (platform.includes('mac') || userAgent.includes('mac')) {
    // Check for Apple Silicon vs Intel
    // This is a heuristic - not 100% accurate
    if (userAgent.includes('arm') || (platform.includes('mac') && !userAgent.includes('intel'))) {
      return 'mac';
    }
    return 'mac-intel';
  }
  if (platform.includes('win') || userAgent.includes('win')) return 'windows';
  if (platform.includes('linux') || userAgent.includes('linux')) return 'linux';

  return 'unknown';
}

const platformInfo = {
  mac: {
    name: 'macOS (Apple Silicon)',
    file: 'Audiio-arm64.dmg',
    icon: DesktopIcon,
  },
  'mac-intel': {
    name: 'macOS (Intel)',
    file: 'Audiio-x64.dmg',
    icon: DesktopIcon,
  },
  windows: {
    name: 'Windows',
    file: 'Audiio-Setup.exe',
    icon: DesktopIcon,
  },
  linux: {
    name: 'Linux',
    file: 'Audiio.AppImage',
    icon: DesktopIcon,
  },
  unknown: {
    name: 'Desktop',
    file: '',
    icon: DesktopIcon,
  },
};

const GITHUB_RELEASES_URL = 'https://github.com/magicianjarden/audiio-official/releases/latest';
// Remote portal hosted on GitHub Pages
const MOBILE_REMOTE_URL = 'https://magicianjarden.github.io/audiio-official/remote/';

export function Download() {
  const [platform, setPlatform] = useState<Platform>('unknown');
  const { isDevMode } = useDevMode();

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  const currentPlatform = platformInfo[platform];
  const Icon = currentPlatform.icon;

  const otherPlatforms = Object.entries(platformInfo)
    .filter(([key]) => key !== platform && key !== 'unknown')
    .map(([key, info]) => ({ key, ...info }));

  return (
    <section id="download" className="download">
      <div className="download-bg">
        <div className="download-glow" />
      </div>

      <div className="container">
        <div className="download-content">
          <h2 className="download-title">
            Ready to{' '}
            <span className="text-gradient">Get Started?</span>
          </h2>
          <p className="download-subtitle">
            Download Audiio for free. No account required.
          </p>

          <div className="download-main">
            <a
              href={GITHUB_RELEASES_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="download-primary btn btn-primary"
            >
              <Icon size={24} />
              <div className="download-info">
                <span className="download-label">Download for {currentPlatform.name}</span>
                {currentPlatform.file && (
                  <span className="download-file">{currentPlatform.file}</span>
                )}
              </div>
              <DownloadIcon size={20} />
            </a>
          </div>

          <div className="download-other">
            <span className="download-other-label">Also available for:</span>
            <div className="download-other-links">
              {otherPlatforms.map((p) => (
                <a
                  key={p.key}
                  href={GITHUB_RELEASES_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-other-link"
                >
                  <p.icon size={16} />
                  {p.name}
                </a>
              ))}
            </div>
          </div>

          <div className="download-mobile">
            <div className="mobile-card">
              <PhoneIcon size={24} />
              <div className="mobile-info">
                <h3>Mobile Remote Access</h3>
                <p>
                  Control your desktop library from anywhere. Enable mobile access in the app,
                  then enter your connection code on the web portal.
                </p>
                <a
                  href={MOBILE_REMOTE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mobile-portal-link"
                >
                  Open Remote Portal <ExternalLinkIcon size={14} />
                </a>
              </div>
            </div>

            <div className="mobile-how-it-works">
              <h4>How P2P Remote Works</h4>
              <ol>
                <li>Enable Mobile Access in desktop app settings</li>
                <li>Get your connection code (e.g., SWIFT-EAGLE-42)</li>
                <li>Visit the remote portal from any device</li>
                <li>Enter your code — connects via our relay server</li>
                <li>Your music streams directly from your desktop</li>
              </ol>
            </div>

            <div className="mobile-tech-specs">
              <h4>Under the Hood</h4>
              <div className="tech-grid">
                <div className="tech-item">
                  <LockIcon size={18} />
                  <div className="tech-content">
                    <span className="tech-label">End-to-End Encrypted</span>
                    <span className="tech-detail">NaCl X25519 + XSalsa20-Poly1305</span>
                  </div>
                </div>
                <div className="tech-item">
                  <CloudOffIcon size={18} />
                  <div className="tech-content">
                    <span className="tech-label">Zero Cloud Storage</span>
                    <span className="tech-detail">Streams direct from your machine</span>
                  </div>
                </div>
                <div className="tech-item">
                  <ZapIcon size={18} />
                  <div className="tech-content">
                    <span className="tech-label">WebSocket Signaling</span>
                    <span className="tech-detail">Sub-second connection establishment</span>
                  </div>
                </div>
                <div className="tech-item">
                  <GlobeIcon size={18} />
                  <div className="tech-content">
                    <span className="tech-label">Self-Hosted Relay</span>
                    <span className="tech-detail">Open-source, auditable, no third parties</span>
                  </div>
                </div>
              </div>
              {isDevMode && (
                <div className="tech-code">
                  <code>
                    # The relay server is open source
                    <br />
                    npm install @audiio/relay
                    <br />
                    npx audiio-relay --port 9484
                  </code>
                </div>
              )}
            </div>
          </div>

          {isDevMode && (
            <div className="download-dev">
              <code>
                # Clone and build from source
                <br />
                git clone https://github.com/magicianjarden/audiio-official.git
                <br />
                cd audiio && npm install && npm run build
              </code>
            </div>
          )}

          <a
            href={GITHUB_RELEASES_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="download-releases-link"
          >
            View all releases on GitHub <ExternalLinkIcon size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
